<template>
  <iframe
    ref="frame"
    class="ae-d3-insight"
    :style="{ height: height + 'px' }"
    sandbox="allow-scripts"
    referrerpolicy="no-referrer"
    :srcdoc="document"
    title="Visualization"
  />
</template>

<script>
// A visualisation the agent wrote itself, rather than one of the four
// registered chart types. The agent's D3 runs in a sandboxed frame with an
// opaque origin: no network, no forms, no navigation, no access to this app.
// It receives the insight's own rows — the same governed rows every other
// insight renders — as a JSON literal, so it cannot fetch anything of its own.
//
// This exists because the registry is a real wall: a viz_type naming an
// unregistered component renders nothing, silently. Rather than add a Vue
// component per bespoke chart, one component runs the code the agent wrote.
const D3_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/d3/7.9.0/d3.min.js';

export default {
  name: 'D3Insight',
  props: {
    // The agent's code. Runs with `data` (the rows) and `el` (a mount div) in scope.
    code: { type: String, required: true },
    rows: { type: Array, default: () => [] },
    minHeight: { type: Number, default: 320 },
  },
  data() {
    return { height: this.minHeight };
  },
  computed: {
    document() {
      const rows = JSON.stringify(this.rows || []);
      return `<!doctype html><html><head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy"
      content="default-src 'none'; style-src 'unsafe-inline'; img-src data:; script-src 'unsafe-inline' https://cdnjs.cloudflare.com;">
<style>
  /* The same tokens the HTML card surface defines. The agent is told these
     exist and writes var(--ink) etc; undefined they resolve to nothing and
     the whole chart renders black on black. */
  :root {
    --ink:#1A1A2E; --body:#3C4257; --muted:#6B7280; --faint:#8A8FA0;
    --paper:#FAF9F6; --card:#FFFFFF; --hairline:#F0EFEC;
    --neutral-rail:#DAD8D2; --neutral-panel:#F5F4F1;
    --accent:#EF6C3B; --link:#C25326;
    --risk:#C0392B; --risk-tint:#FBEAE6;
    --attention:#B08A3A; --attention-tint:#FBF1E1;
    --stable:#2F6B49; --stable-tint:#E9F3ED;
    --info:#3E5C8A; --info-tint:#EEF1F7; --snf:#7A5EA8;
  }
  html, body { margin: 0; max-width: 100%; overflow-x: hidden; }
  body { font: 13px -apple-system, system-ui, sans-serif; color: #38352f; background: #fff; }
  #chart { width: 100%; max-width: 100%; }
  /* The agent's code may size an SVG in fixed pixels. Scale it down to the
     panel rather than letting it push out a horizontal scrollbar. */
  #chart svg { max-width: 100%; height: auto; display: block; }
  .err { padding: 12px; color: #c23a28; font-family: 'IBM Plex Mono', monospace; font-size: 12px; white-space: pre-wrap; }
</style>
<script src="${D3_SRC}"><\/script>
</head><body>
<div id="chart"></div>
<script>
(function () {
  var data = ${rows};
  var el = document.getElementById('chart');
  var width = el.clientWidth;
  var post = function () {
    parent.postMessage({ d3insight: 'height', value: document.documentElement.scrollHeight }, '*');
  };
  // An SVG sized in fixed pixels cannot shrink. Give it a viewBox matching
  // the size the agent chose, so max-width scales it into the panel instead
  // of pushing out a horizontal scrollbar.
  var fit = function () {
    el.querySelectorAll('svg').forEach(function (svg) {
      if (!svg.getAttribute('viewBox')) {
        var w = parseFloat(svg.getAttribute('width'));
        var h = parseFloat(svg.getAttribute('height'));
        if (w > 0 && h > 0) svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
      }
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      svg.setAttribute('preserveAspectRatio', 'xMinYMin meet');
    });
  };
  try {
    ${this.code}
  } catch (e) {
    el.innerHTML = '<div class="err">' + (e && e.message ? e.message : String(e)) + '<\\/div>';
  }
  fit();
  new ResizeObserver(post).observe(document.documentElement);
  post();
})();
<\/script>
</body></html>`;
    },
  },
  mounted() {
    window.addEventListener('message', this.onMessage);
  },
  beforeUnmount() {
    window.removeEventListener('message', this.onMessage);
  },
  methods: {
    onMessage(event) {
      const frame = this.$refs.frame;
      if (!frame || event.source !== frame.contentWindow) return;
      if ((event.data || {}).d3insight === 'height') {
        this.height = Math.max(this.minHeight, Number(event.data.value) || this.minHeight);
      }
    },
  },
};
</script>
